import { Bookmark, ChevronRight, ExternalLink, Search } from "lucide-react";
import posthog from "posthog-js";
import React from "react";

import { Button } from "@/app/components/ui/button";
import { wrapLinksAndEmails } from "@/app/handlers/valueTransformation.js";

import { ApplicationDetails, isJobExpired, JobDetails } from "../utils/helpers";

interface ApplicationProcessProps {
	jobDetails: JobDetails;
	applicationDetails: ApplicationDetails;
	scrollToEmail: boolean;
	applicationRef: React.RefObject<HTMLDivElement>;
	onApply: (link: string | null) => void;
	onSave?: () => void;
	hasViewedExplainer?: boolean;
	hasCompletedFitCheck?: boolean;
	onExplore: () => void;
}

export function ApplicationProcess({
	jobDetails,
	applicationDetails,
	onExplore,
	applicationRef,
	onApply,
	onSave,
	hasViewedExplainer = false,
	hasCompletedFitCheck = false,
}: ApplicationProcessProps) {
	// Track apply click with context
	const handleApplyClick = (link: string | null) => {
		// Determine apply context
		const eventName = hasCompletedFitCheck
			? "apply_clicked_after_fitcheck"
			: hasViewedExplainer
				? "apply_clicked_after_explainer"
				: "apply_clicked_direct";

		posthog.capture(eventName, {
			jobId: jobDetails?._id,
			jobTitle: jobDetails?.jobTitle,
			companyName: jobDetails?.company?.company_name,
			companyId: jobDetails?.company?._id,
			hasViewedExplainer,
			hasCompletedFitCheck,
		});

		onApply(link);
	};

	if (!jobDetails?.applicationProcess || jobDetails?.applicationProcess === "" || isJobExpired(jobDetails.closeAt)) {
		return null;
	}

	return (
		<div
			className="bg-blue-50/50 rounded-lg p-6 mb-8 border border-blue-100 grid grid-cols-8"
			id="applicationProcess"
			ref={applicationRef}
		>
			{/* Commitment Zone Header */}
			<div className="col-span-6">
				<p className="text-lg font-semibold text-gray-900 mb-1">How to apply</p>

				{/* Application Instructions */}
				<div
					className="text-gray-600 font-inter text-sm content-wrapper"
					dangerouslySetInnerHTML={{
						__html: wrapLinksAndEmails(jobDetails?.applicationProcess),
					}}
				/>
			</div>

			{/* Primary Apply CTA */}
			<div className="flex flex-col gap-3 col-span-2">
				{applicationDetails.applicationType === "URL" && (
					<Button
						onClick={() => handleApplyClick(applicationDetails.link)}
						className="bg-primary-700 hover:bg-primary-800 text-white flex-1 sm:flex-initial"
					>
						Apply for this job
						<ExternalLink className="h-4 w-4 ml-2" />
					</Button>
				)}

				{applicationDetails.applicationType === "EMAIL" && (
					<Button
						onClick={() => handleApplyClick(applicationDetails.link)}
						className="bg-primary-700 hover:bg-primary-800 text-white flex-1 sm:flex-initial"
					>
						Apply for this job
						<ChevronRight className="h-4 w-4 ml-2" />
					</Button>
				)}

				{/* Secondary Actions */}
				{onSave && (
					<Button onClick={onSave} variant="secondary" size="default">
						<Bookmark className="h-4 w-4 mr-2" />
						Save for later
					</Button>
				)}

				<Button onClick={onExplore} variant="link">
					<Search className="h-4 w-4 mr-2" />
					Explore similar roles
				</Button>
			</div>
		</div>
	);
}
